import { useAppEnvironmentContext } from '../contexts/appEnvironmentContext';
import { useOfficeContext } from './addin/office';
import { websiteLinkTo } from './routes';

/*
 * This file provides tools to open and close popup windows, switching to the Office API's displayDialogAsync
 * method when running in the Office desktop app (since that's the only way to open a popup window there).
 *
 * Currently this mechanism is used for Microsoft and Google SSO logins on any platform through following steps:
 *   1. Use usePopupWindowOpener to get a function that opens a popup window to either Microsoft of Google
 *      internal login redirect path (either via windw.open or Office API, depending on the environment).
 *   2. The redirect path is ALWAYS a website link (/login/google or /login/microsoft) because the URL is not
 *      supposed to contain any hash params (which the add-in links require for their paths) since Google
 *      redirects don't support hash params.
 *   3. The redirect path pages (SignIn*RedirectPage components) use Google and Microsoft libraries to fetch
 *      user credentials via redirect mechanism (which happens within the popup window), which is used to
 *      authenticate the user through our own API.
 *   4. Unfortunately, the popup window won't know whether it's being used in the Office context or not, so
 *      it won't be able to send correct X-Host & X-Platform data to the API. This means we'll need to populate
 *      signed_up_via post-signup via a separate API call from the main app window after the popup window closes.
 *   5. Finally, the popup window is closed by calling closePopupWindow, which first tries to opportunistically
 *      close the window if not opened through Office Dialog API (remember, the popup doesn't know who opened it)
 *      and if window.close doesn't work, it redirects to a special add-in page that sends a message to the parent
 *      window to close the dialog (the add-in page redirect is necessary to load in Office API to send the message).
 *
 * This was developped under a whole bunch of constraints and is a bit of a mess, but it seems to work universally.
 * Some improvements are most certainly possible, but this should be a good starting point and the UX is pretty good.
 *
 * The next steps will be to:
 *   - Use this for all Microsoft and Google sign-in buttons everywhere in the app
 *   - Use this also for Stripe subscribe step redirect so it can be done from the Office add-in directly
 */

// TODO: Use this for Microsoft / Google sign-in buttons EVERYWHERE
export const usePopupWindowOpener = (path: string) => {
  const { appEnvironment } = useAppEnvironmentContext();
  const { isOfficeOnline } = useOfficeContext();

  // Only show the Office dialog if we're in the Office desktop app
  const showOfficeDialog = appEnvironment === 'office' && !isOfficeOnline;
  const url = websiteLinkTo(path);

  return () => {
    if (showOfficeDialog) {
      Office.context.ui.displayDialogAsync(
        url,
        { height: 40, width: 20 },
        ({ value: dialog }) => {
          // Close the dialog when any message is received
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () =>
            dialog.close()
          );
        }
      );
    } else {
      window.open(url, '_blank', 'width=420,height=640');
    }
  };
};

export const closePopupWindow = () => {
  // First try to opportunistically close the window (if not opened through Office Dialog API)
  window.close();
  // Then redirect to an add-in page that sends a message to the parent to close the dialog.
  // NOTE: We do this because generally we won't be able to use Office API to contact the
  // the parent window from the popup window, UNLESS we're in the Office add-in context so
  // we use it as a proxy to close the popup.
  window.location.href = websiteLinkTo('/static/office/close-dialog.html');
};
